import { bindServices } from '@/services/api';
import { toPaginate } from '@/utils/infrastructure';
import _ from 'lodash';

// Bind API services to namespace
const api = bindServices('applications');

const meta = { page: 1, limit: 5, count: 0, totalPages: 0 };
const commonState = {
  data: [],
  loading: false,
  lastFetched: null,
  processInstanceId: null,
};

const initialState = {
  documents: { ...commonState, actionInstanceId: null, pagination: meta, orderBy: 'created desc' },
  comments: { ...commonState },
  generatedDocuments: { ...commonState },
  mailsTracking: {
    ...commonState,
    actionInstanceId: null,
    pagination: meta,
    orderBy: 'created desc',
  },
  engineTasks: {
    ...commonState,
    actionInstanceId: null,
    pagination: meta,
    orderBy: 'created desc',
  },
  reassignedTaskTraces: {
    ...commonState,
    actionInstanceId: null,
    pagination: meta,
    orderBy: 'created desc',
  },
  commandTraces: {
    ...commonState,
    actionInstanceId: null,
    pagination: meta,
    orderBy: 'created desc',
  },
  engineTaskDetail: { data: null, loading: false },
  reassignedTaskTraceDetail: { data: null, loading: false },
  commandTraceDetail: { data: null, loading: false },

  variables: {
    data: [],
    loading: false,
    pagination: meta,
    orderBy: '',
    lastFetchedParams: null,
  },

  forms: {
    additionalConfig: {},
    loading: false,
    processInstanceId: null,
    actionInstanceId: null,
  },

  invalidations: {
    documents: null,
    comments: null,
    generatedDocuments: null,
    mailsTracking: null,
    engineTasks: null,
    reassignedTaskTraces: null,
    commandTraces: null,
    forms: null,
    variables: null,
  },
};

const Model = {
  namespace: 'applicationTabs',
  state: initialState,
  effects: {
    *fetchDocuments({ payload }, { call, put, select }) {
      const {
        processInstanceId,
        actionInstanceId,
        page,
        limit,
        search,
        orderBy,
        force = false,
      } = payload;

      // Check cache validity
      const cached = yield select((state) => state.applicationTabs.documents);
      const invalidationTime = yield select(
        (state) => state.applicationTabs.invalidations.documents,
      );

      const cacheValid =
        cached.processInstanceId === processInstanceId &&
        cached.actionInstanceId === actionInstanceId &&
        cached.lastFetched &&
        Date.now() - cached.lastFetched < 60000 && // 1min cache
        (!invalidationTime || invalidationTime < cached.lastFetched);

      if (cacheValid && !force) return;

      try {
        yield put({ type: 'setDocumentsLoading', payload: true });
        const response = yield call(api.getData, payload);
        yield put({
          type: 'saveDocuments',
          payload: {
            data: response.items,
            processInstanceId,
            actionInstanceId,
            page,
            limit,
            totalCount: response.totalCount,
          },
        });
      } catch (e) {}
    },

    *fetchComments({ payload }, { call, put, select }) {
      const { processInstanceId, force = false } = payload;

      // Check cache validity
      const cached = yield select((state) => state.applicationTabs.comments);
      const invalidationTime = yield select(
        (state) => state.applicationTabs.invalidations.comments,
      );

      const cacheValid =
        cached.processInstanceId === processInstanceId &&
        cached.lastFetched &&
        Date.now() - cached.lastFetched < 60000 &&
        (!invalidationTime || invalidationTime < cached.lastFetched);

      if (cacheValid && !force) return;

      try {
        yield put({ type: 'setCommentsLoading', payload: true });
        const response = yield call(api.getData, payload);
        yield put({
          type: 'saveComments',
          payload: {
            data: response,
            processInstanceId,
          },
        });
      } catch (e) {}
    },

    *addComment({ payload }, { call, put }) {
      try {
        const { processInstanceId, actionInstanceId, ...data } = payload;
        const response = yield call(api.create, {
          route: `processInstance/data/addComment/${processInstanceId}/${actionInstanceId}`,
          data,
        });
        yield put({ type: 'prependComment', payload: response });
      } catch (e) {}
    },

    *updateComment({ payload }, { call, put }) {
      try {
        const { processInstanceId, commentId, data } = payload;
        const response = yield call(api.update, {
          route: `processInstance/data/comments/${processInstanceId}/${commentId}`,
          data,
        });
        yield put({ type: 'replaceComment', payload: response });
      } catch (e) {}
    },

    *deleteComment({ payload }, { call, put }) {
      try {
        const { processInstanceId, commentId } = payload;
        const response = yield call(api.remove, {
          route: `processInstance/data/comments`,
          id: `${processInstanceId}/${commentId}`,
        });
        yield put({ type: 'removeComment', payload });
      } catch (e) {}
    },

    *fetchGeneratedDocuments({ payload }, { call, put, select }) {
      const { processInstanceId, force = false } = payload;

      const cached = yield select((state) => state.applicationTabs.generatedDocuments);
      const invalidationTime = yield select(
        (state) => state.applicationTabs.invalidations.generatedDocuments,
      );

      const cacheValid =
        cached.processInstanceId === processInstanceId &&
        cached.lastFetched &&
        Date.now() - cached.lastFetched < 60000 &&
        (!invalidationTime || invalidationTime < cached.lastFetched);

      if (cacheValid && !force) {
        return;
      }

      try {
        yield put({ type: 'setGeneratedDocumentsLoading', payload: true });
        const response = yield call(api.getData, payload);
        yield put({
          type: 'saveGeneratedDocuments',
          payload: { data: response, processInstanceId },
        });
      } catch (e) {}
    },

    *fetchMailsTracking({ payload }, { call, put, select }) {
      const { processInstanceId, actionInstanceId, page, limit, force = false } = payload;

      const cached = yield select((state) => state.applicationTabs.mailsTracking);
      const invalidationTime = yield select(
        (state) => state.applicationTabs.invalidations.mailsTracking,
      );

      const cacheValid =
        cached.processInstanceId === processInstanceId &&
        cached.actionInstanceId === actionInstanceId &&
        cached.lastFetched &&
        Date.now() - cached.lastFetched < 60000 &&
        (!invalidationTime || invalidationTime < cached.lastFetched);

      if (cacheValid && !force) return;

      try {
        yield put({ type: 'setMailsTrackingLoading', payload: true });
        const response = yield call(api.getData, payload);
        yield put({
          type: 'saveMailsTracking',
          payload: {
            data: response.items,
            processInstanceId,
            actionInstanceId,
            page,
            limit,
            totalCount: response.totalCount,
          },
        });
      } catch (e) {}
    },

    *fetchEngineTasks({ payload }, { call, put, select }) {
      const { processInstanceId, actionInstanceId, page, limit, force = false } = payload;

      const cached = yield select((state) => state.applicationTabs.engineTasks);
      const invalidationTime = yield select(
        (state) => state.applicationTabs.invalidations.engineTasks,
      );

      const cacheValid =
        cached.processInstanceId === processInstanceId &&
        cached.actionInstanceId === actionInstanceId &&
        cached.lastFetched &&
        Date.now() - cached.lastFetched < 60000 &&
        (!invalidationTime || invalidationTime < cached.lastFetched);

      if (cacheValid && !force) return;

      try {
        yield put({ type: 'setEngineTasksLoading', payload: true });
        const response = yield call(api.getData, payload);
        yield put({
          type: 'saveEngineTasks',
          payload: {
            data: response.items,
            processInstanceId,
            actionInstanceId,
            page,
            limit,
            totalCount: response.totalCount,
          },
        });
      } catch (e) {}
    },

    *fetchReassignedTaskTraces({ payload }, { call, put, select }) {
      const { processInstanceId, actionInstanceId, page, limit, force = false } = payload;

      const cached = yield select((state) => state.applicationTabs.reassignedTaskTraces);
      const invalidationTime = yield select(
        (state) => state.applicationTabs.invalidations.reassignedTaskTraces,
      );

      const cacheValid =
        cached.processInstanceId === processInstanceId &&
        cached.actionInstanceId === actionInstanceId &&
        cached.lastFetched &&
        Date.now() - cached.lastFetched < 60000 &&
        (!invalidationTime || invalidationTime < cached.lastFetched);

      if (cacheValid && !force) return;

      try {
        yield put({ type: 'setReassignedTaskTracesLoading', payload: true });
        const response = yield call(api.getData, payload);
        yield put({
          type: 'saveReassignedTaskTraces',
          payload: {
            data: response.items,
            processInstanceId,
            actionInstanceId,
            page,
            limit,
            totalCount: response.totalCount,
          },
        });
      } catch (e) {}
    },

    *fetchCommandTraces({ payload }, { call, put, select }) {
      const { processInstanceId, actionInstanceId, page, limit, force = false } = payload;

      const cached = yield select((state) => state.applicationTabs.commandTraces);
      const invalidationTime = yield select(
        (state) => state.applicationTabs.invalidations.commandTraces,
      );

      const cacheValid =
        cached.processInstanceId === processInstanceId &&
        cached.actionInstanceId === actionInstanceId &&
        cached.lastFetched &&
        Date.now() - cached.lastFetched < 60000 &&
        (!invalidationTime || invalidationTime < cached.lastFetched);

      if (cacheValid && !force) return;

      try {
        yield put({ type: 'setCommandTracesLoading', payload: true });
        const response = yield call(api.getData, payload);
        yield put({
          type: 'saveCommandTraces',
          payload: {
            data: response.items,
            processInstanceId,
            actionInstanceId,
            page,
            limit,
            totalCount: response.totalCount,
          },
        });
      } catch (e) {}
    },

    *fetchEngineTaskDetail({ payload }, { call, put }) {
      try {
        yield put({ type: 'setEngineTaskDetailLoading', payload: true });
        const response = yield call(api.getById, {
          route: 'processEngineInternalTask',
          ...payload,
        });
        yield put({ type: 'saveEngineTaskDetail', payload: response });
      } catch (e) {}
    },

    *fetchReassignedTaskTraceDetail({ payload }, { call, put }) {
      try {
        yield put({ type: 'setReassignedTaskTraceDetailLoading', payload: true });
        const response = yield call(api.getData, {
          route: 'reassignedTaskTrace',
          processInstanceId: payload.processInstanceId,
          tableId: payload.reassignedTaskTraceId,
          expand: {
            sourceUser: { select: 'fullName' },
            destinationUser: { select: 'fullName' },
            userCreator: { select: 'fullName' },
          },
        });
        yield put({ type: 'saveReassignedTaskTraceDetail', payload: response });
      } catch (e) {}
    },

    *fetchCommandTraceDetail({ payload }, { call, put }) {
      try {
        yield put({ type: 'setCommandTraceDetailLoading', payload: true });
        const response = yield call(api.getData, {
          route: 'commandTrace',
          processInstanceId: payload.processInstanceId,
          tableId: payload.commandTraceId,
          select: 'id,created,commandType,creator,data',
        });
        yield put({ type: 'saveCommandTraceDetail', payload: response });
      } catch (e) {}
    },

    *fetchVariables({ payload }, { call, put, select }) {
      try {
        yield put({ type: 'setVariablesLoading', payload: true });
        const response = yield call(api.getData, { route: `variable/paged`, ...payload });
        const variables = yield select((st) => st.applications.currentItem?.process?.variables);

        yield put({
          type: 'saveVariables',
          payload: {
            items: response.items,
            page: payload.page,
            limit: payload.limit,
            totalCount: response.totalCount,
            variables,
          },
        });

        yield put({ type: 'saveVariablesLastFetchedParams', payload });
      } catch (e) {}
    },

    *repeatLatestFetchVariables(__, { put, select }) {
      const latestPayload = yield select(
        (state) => state.applicationTabs.variables.lastFetchedParams,
      );
      if (latestPayload) {
        yield put({ type: 'fetchVariables', payload: latestPayload });
      }
    },

    *setVariablesOrderBy({ payload }, { put }) {
      yield put({ type: 'updateVariablesOrderBy', payload });
    },

    *fetchFormsConfig({ payload }, { call, put, select }) {
      const { processInstanceId, actionInstanceId, formId, force = false } = payload;

      // Check if this specific form config is cached
      const cached = yield select((state) => state.applicationTabs.forms);
      const invalidationTime = yield select((state) => state.applicationTabs.invalidations.forms);

      const hasFormCache = cached.additionalConfig?.[formId];
      const cacheValid =
        hasFormCache &&
        cached.processInstanceId === processInstanceId &&
        cached.actionInstanceId === actionInstanceId &&
        (!invalidationTime || invalidationTime < cached.lastFetched);

      if (cacheValid && !force) return;

      try {
        yield put({ type: 'setFormsLoading', payload: true });
        const response = yield call(api.getData, payload);
        yield put({
          type: 'saveFormsConfig',
          payload: {
            config: response,
            formId,
            processInstanceId,
            actionInstanceId,
          },
        });
      } catch (e) {}
    },

    *invalidateTab({ payload }, { put }) {
      const { tab } = payload;
      yield put({
        type: 'markInvalidated',
        payload: { tab, timestamp: Date.now() },
      });
    },

    *invalidateAll(_, { put }) {
      const tabs = [
        'documents',
        'comments',
        'generatedDocuments',
        'mailsTracking',
        'engineTasks',
        'reassignedTaskTraces',
        'commandTraces',
        'forms',
        'variables',
      ];
      for (const tab of tabs) {
        yield put({ type: 'markInvalidated', payload: { tab, timestamp: Date.now() } });
      }
    },

    *cleanup(o, { put }) {
      yield put({ type: 'resetState' });
    },
  },

  reducers: {
    saveDocuments(state, { payload }) {
      return {
        ...state,
        documents: {
          ...state.documents,
          data: payload.data,
          loading: false,
          lastFetched: Date.now(),
          processInstanceId: payload.processInstanceId,
          actionInstanceId: payload.actionInstanceId,
          pagination: {
            ...state.documents.pagination,
            ...toPaginate(payload),
          },
        },
      };
    },

    setDocumentsLoading(state, { payload }) {
      return {
        ...state,
        documents: { ...state.documents, loading: payload },
      };
    },

    setDocumentsOrderBy(state, { payload }) {
      return {
        ...state,
        documents: {
          ...state.documents,
          orderBy: payload || 'created desc',
        },
      };
    },

    saveComments(state, { payload }) {
      return {
        ...state,
        comments: {
          data: payload.data,
          loading: false,
          lastFetched: Date.now(),
          processInstanceId: payload.processInstanceId,
        },
      };
    },

    setCommentsLoading(state, { payload }) {
      return {
        ...state,
        comments: { ...state.comments, loading: payload },
      };
    },

    prependComment(state, { payload }) {
      return {
        ...state,
        comments: {
          ...state.comments,
          data: [payload, ...state.comments.data],
        },
      };
    },

    replaceComment(state, { payload }) {
      return {
        ...state,
        comments: {
          ...state.comments,
          data: _.map(state.comments.data, (item) => (item.id === payload.id ? payload : item)),
        },
      };
    },

    removeComment(state, { payload }) {
      return {
        ...state,
        comments: {
          ...state.comments,
          data: _.filter(state.comments.data, (item) => item.id !== payload.commentId),
        },
      };
    },

    saveGeneratedDocuments(state, { payload }) {
      return {
        ...state,
        generatedDocuments: {
          data: payload.data,
          loading: false,
          lastFetched: Date.now(),
          processInstanceId: payload.processInstanceId,
        },
      };
    },

    setGeneratedDocumentsLoading(state, { payload }) {
      return {
        ...state,
        generatedDocuments: { ...state.generatedDocuments, loading: payload },
      };
    },

    saveMailsTracking(state, { payload }) {
      return {
        ...state,
        mailsTracking: {
          ...state.mailsTracking,
          data: payload.data,
          loading: false,
          lastFetched: Date.now(),
          processInstanceId: payload.processInstanceId,
          actionInstanceId: payload.actionInstanceId,
          pagination: {
            ...state.mailsTracking.pagination,
            ...toPaginate(payload),
          },
        },
      };
    },

    setMailsTrackingLoading(state, { payload }) {
      return {
        ...state,
        mailsTracking: { ...state.mailsTracking, loading: payload },
      };
    },

    setMailsTrackingOrderBy(state, { payload }) {
      return {
        ...state,
        mailsTracking: {
          ...state.mailsTracking,
          orderBy: payload || 'created desc',
        },
      };
    },

    saveEngineTasks(state, { payload }) {
      return {
        ...state,
        engineTasks: {
          ...state.engineTasks,
          data: payload.data,
          loading: false,
          lastFetched: Date.now(),
          processInstanceId: payload.processInstanceId,
          actionInstanceId: payload.actionInstanceId,
          pagination: {
            ...state.engineTasks.pagination,
            ...toPaginate(payload),
          },
        },
      };
    },

    setEngineTasksLoading(state, { payload }) {
      return {
        ...state,
        engineTasks: { ...state.engineTasks, loading: payload },
      };
    },

    setEngineTasksOrderBy(state, { payload }) {
      return {
        ...state,
        engineTasks: {
          ...state.engineTasks,
          orderBy: payload || 'created desc',
        },
      };
    },

    saveReassignedTaskTraces(state, { payload }) {
      return {
        ...state,
        reassignedTaskTraces: {
          ...state.reassignedTaskTraces,
          data: payload.data,
          loading: false,
          lastFetched: Date.now(),
          processInstanceId: payload.processInstanceId,
          actionInstanceId: payload.actionInstanceId,
          pagination: {
            ...state.reassignedTaskTraces.pagination,
            ...toPaginate(payload),
          },
        },
      };
    },

    setReassignedTaskTracesLoading(state, { payload }) {
      return {
        ...state,
        reassignedTaskTraces: { ...state.reassignedTaskTraces, loading: payload },
      };
    },

    setReassignedTaskTracesOrderBy(state, { payload }) {
      return {
        ...state,
        reassignedTaskTraces: {
          ...state.reassignedTaskTraces,
          orderBy: payload || 'created desc',
        },
      };
    },

    saveCommandTraces(state, { payload }) {
      return {
        ...state,
        commandTraces: {
          ...state.commandTraces,
          data: payload.data,
          loading: false,
          lastFetched: Date.now(),
          processInstanceId: payload.processInstanceId,
          actionInstanceId: payload.actionInstanceId,
          pagination: {
            ...state.commandTraces.pagination,
            ...toPaginate(payload),
          },
        },
      };
    },

    setCommandTracesLoading(state, { payload }) {
      return {
        ...state,
        commandTraces: { ...state.commandTraces, loading: payload },
      };
    },

    setCommandTracesOrderBy(state, { payload }) {
      return {
        ...state,
        commandTraces: {
          ...state.commandTraces,
          orderBy: payload || 'created desc',
        },
      };
    },

    saveEngineTaskDetail(state, { payload }) {
      return {
        ...state,
        engineTaskDetail: {
          data: payload,
          loading: false,
        },
      };
    },

    setEngineTaskDetailLoading(state, { payload }) {
      return {
        ...state,
        engineTaskDetail: { ...state.engineTaskDetail, loading: payload },
      };
    },

    saveReassignedTaskTraceDetail(state, { payload }) {
      return {
        ...state,
        reassignedTaskTraceDetail: {
          data: payload,
          loading: false,
        },
      };
    },

    setReassignedTaskTraceDetailLoading(state, { payload }) {
      return {
        ...state,
        reassignedTaskTraceDetail: { ...state.reassignedTaskTraceDetail, loading: payload },
      };
    },

    saveCommandTraceDetail(state, { payload }) {
      return {
        ...state,
        commandTraceDetail: {
          data: payload,
          loading: false,
        },
      };
    },

    setCommandTraceDetailLoading(state, { payload }) {
      return {
        ...state,
        commandTraceDetail: { ...state.commandTraceDetail, loading: payload },
      };
    },

    saveVariables(state, { payload }) {
      const data = _.map(payload.items, (o) => {
        return {
          ..._.find(payload.variables, { id: o.variableId }),
          ...o,
        };
      });

      return {
        ...state,
        variables: {
          ...state.variables,
          data,
          loading: false,
          pagination: {
            ...state.variables.pagination,
            ...toPaginate(payload),
          },
        },
      };
    },

    setVariablesLoading(state, { payload }) {
      return {
        ...state,
        variables: { ...state.variables, loading: payload },
      };
    },

    updateVariablesOrderBy(state, { payload }) {
      return {
        ...state,
        variables: {
          ...state.variables,
          orderBy: payload,
        },
      };
    },

    saveVariablesLastFetchedParams(state, { payload }) {
      return {
        ...state,
        variables: {
          ...state.variables,
          lastFetchedParams: payload,
        },
      };
    },

    saveFormsConfig(state, { payload }) {
      return {
        ...state,
        forms: {
          additionalConfig: {
            ...state.forms.additionalConfig,
            [payload.formId]: payload.config,
          },
          loading: false,
          lastFetched: Date.now(),
          processInstanceId: payload.processInstanceId,
          actionInstanceId: payload.actionInstanceId,
        },
      };
    },

    setFormsLoading(state, { payload }) {
      return {
        ...state,
        forms: { ...state.forms, loading: payload },
      };
    },

    markInvalidated(state, { payload }) {
      const { tab, timestamp } = payload;
      return {
        ...state,
        invalidations: {
          ...state.invalidations,
          [tab]: timestamp,
        },
      };
    },

    resetState() {
      return initialState;
    },
  },
};

export default Model;
